import type { AIImportPlan, AIImportPlanContact, AIImportQuestion } from '@/types/ai-import';

/**
 * Drop reviewer-skipped properties from a plan, keeping index-based references
 * honest. contacts[].property_indexes and valuations[].subject_plan_index point
 * INTO plan.properties and ai-import-apply resolves them positionally, so
 * filtering the array alone silently re-aims a contact link — or a subject sale
 * — at whichever property slid into the freed slot. A reference whose property
 * was skipped is cleared, not repointed: apply then falls back to the primary
 * property (contacts) or name matching (valuations).
 */
export function dropPlanProperties(plan: AIImportPlan, skipped: Set<number>): AIImportPlan {
  if (skipped.size === 0) return plan;

  const moved = new Map<number, number>();
  let next = 0;
  plan.properties.forEach((_, i) => {
    if (!skipped.has(i)) moved.set(i, next++);
  });

  return {
    ...plan,
    properties: plan.properties.filter((_, i) => !skipped.has(i)),
    contacts: (plan.contacts ?? []).map((c) => {
      if (!Array.isArray(c.property_indexes)) return c;
      const kept = c.property_indexes
        .map((i) => moved.get(i))
        .filter((i): i is number => typeof i === 'number');
      return { ...c, property_indexes: kept.length > 0 ? kept : null };
    }),
    valuations: (plan.valuations ?? []).map((v) => {
      const subject = v.property_match?.subject_plan_index;
      if (typeof subject !== 'number') return v;
      return {
        ...v,
        property_match: { ...v.property_match, subject_plan_index: moved.get(subject) ?? null },
      };
    }),
  };
}

/** Identity of a proposed contact: the email if there is one, else the name. */
function contactKey(c: AIImportPlanContact): string {
  return String(c.email ?? c.name ?? '').trim().toLowerCase();
}

/**
 * A contact question ("… names Crystal … Add to contacts?") is a yes/no on a
 * whole person, not a field edit — target_field is null, so the generic path
 * below would drop the answer on the floor. The accepted person belongs in
 * plan.contacts: ai-import-apply reads that array and never reads question
 * rows, so a contact that does not land there is simply never created.
 *
 * "Fix name…" resolves with the corrected NAME as a plain string; fold it onto
 * the proposed contact rather than losing the email, phone, employer and park
 * attribution (mirrors how the worker re-reads accepted contacts on re-assembly).
 */
function foldContactAnswer(
  plan: AIImportPlan,
  question: AIImportQuestion,
  value: unknown,
): AIImportPlan {
  const proposed =
    question.suggested_value && typeof question.suggested_value === 'object'
      ? (question.suggested_value as Record<string, unknown>)
      : null;
  const raw: Record<string, unknown> =
    value && typeof value === 'object'
      ? (value as Record<string, unknown>)
      : { ...(proposed ?? {}), name: String(value) };

  const str = (k: string): string | null => {
    const v = raw[k];
    return typeof v === 'string' && v.trim() !== '' ? v.trim() : null;
  };
  const contact: AIImportPlanContact = {
    name: str('name'),
    email: str('email'),
    phone: str('phone'),
    organization: str('organization'),
    role: str('role') ?? 'other',
    property_indexes: Array.isArray(raw.property_indexes)
      ? (raw.property_indexes as number[])
      : null,
  };
  if (!contactKey(contact)) return plan;

  // Accepting the same person twice (row Accept, then an Accept-all sweep)
  // must not put them in the plan twice.
  const existing = plan.contacts ?? [];
  const at = existing.findIndex((c) => contactKey(c) === contactKey(contact));
  return {
    ...plan,
    contacts:
      at >= 0
        ? existing.map((c, i) => (i === at ? { ...c, ...contact } : c))
        : [...existing, contact],
  };
}

/**
 * Display names for the plan's properties, in plan order. Review surfaces use
 * this so a bulk apply names the parks it is about to commit — a phantom
 * duplicate park is invisible behind a bare "2 new" count. Unnamed entries stay
 * visible as '(unnamed)' rather than dropping out.
 */
export function planPropertyNames(plan: AIImportPlan | null | undefined): string[] {
  return (plan?.properties ?? []).map((p) => {
    const name = String(p.fields_to_apply?.name ?? p.extracted?.name ?? '').trim();
    return name || '(unnamed)';
  });
}

/**
 * Fold a resolved question answer into a plan so it applies. ai-import-apply
 * writes fields_to_apply / deal directly and never reads the question rows, so a
 * correction that only touched the question row would be invisible until a worker
 * re-assembly. Pure: returns a new plan, never mutates the input. An empty value
 * or missing field returns the same plan reference unchanged.
 */
export function foldAnswerIntoPlan(
  plan: AIImportPlan,
  question: AIImportQuestion,
  value: unknown,
): AIImportPlan {
  if (value == null || value === '') return plan;
  if (question.target_entity === 'contact') return foldContactAnswer(plan, question, value);

  const field = question.target_field;
  if (!field) return plan;

  if (question.target_entity === 'deal') {
    return { ...plan, deal: { ...plan.deal, [field]: value } };
  }
  if (question.target_entity !== 'property') return plan;

  const nameFrom = question.question_text.match(/"([^"]+)"/)?.[1] ?? question.context_text ?? '';
  const target = nameFrom.trim().toLowerCase();
  let idx = plan.properties.findIndex((p) => {
    const n = String(p.fields_to_apply.name ?? p.extracted.name ?? '').toLowerCase();
    return !!n && (n === target || n.includes(target) || target.includes(n));
  });
  if (idx < 0 && plan.properties.length === 1) idx = 0;
  if (idx < 0) return plan;

  return {
    ...plan,
    properties: plan.properties.map((p, i) =>
      i === idx ? { ...p, fields_to_apply: { ...p.fields_to_apply, [field]: value } } : p,
    ),
  };
}
